import { TabLabelInterface } from '@/components/TabLabels/TabLabels';
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import { PurchaseOrder as PurchaseOrderModel } from '@/models/PurchaseOrder';
import ErrorHandler from '@/support/ErrorHandler';
import { axiosDownload } from '@/support/Client';
import { PurchaseOrderItem } from '@/models/PurchaseOrderItem';
import { cloneDeep, isFunction } from 'lodash';
import { ActionItem } from '@/components/ActionList/ActionList';
import { OrderStatusEnum, OrderStatusKeyInfo } from '@/items/OrderStatus';
import { Options } from '@/components/mi-dialog/MiDialog';
import { Nmbr } from '@atabix/core';

@Component<PurchaseOrder>({})
export default class PurchaseOrder extends Vue {

  // #region @Props
  // #endregion

  // #region @Refs

  @Ref()
  protected routerView!: any;

  // #endregion

  // #region Props

  protected activeTab = `/purchase-orders/${this.$route.params.id}/order-items`;

  protected isEditing = false;

  protected isLoading = true;

  protected isExportingPurchaseOrder = false;

  protected isShowingBulkUpdateOrderItemsDialog = false;

  protected isShowingCreatePurchaseOrderItemDialog = false;

  protected isShowingImportPurchaseOrderDialog = false;

  protected isShowingQuickAddProductsDialog = false;

  protected purchaseOrder: null | PurchaseOrderModel = null;

  protected selectedPurchaseOrderItems: PurchaseOrderItem[] = [];

  protected tabs: TabLabelInterface[] = [
    {
      title: 'Order items',
      to: `/purchase-orders/${this.$route.params.id}/order-items`,
    },
    {
      title: 'Order summary',
      to: `/purchase-orders/${this.$route.params.id}/order-summary`,
    },
    {
      title: 'Goods in',
      to: `/purchase-orders/${this.$route.params.id}/goods-in`,
    },
    {
      title: 'Attachments',
      to: `/purchase-orders/${this.$route.params.id}/attachments`,
    },
  ];

  // #endregion

  // #region Plugin props

  public $pageTitle = this.pageTitle;

  // #endregion

  // #region Lifecycle hooks / Init

  protected mounted(): void {
    this.initialize();
  }

  protected beforeUpdate(): void {
    this.$pageTitle = this.pageTitle;
  }

  protected async initialize(): Promise<void> {
    this.isLoading = true;

    await this.fetchPurchaseOrder();

    this.isLoading = false;
  }

  // #endregion

  // #region Handlers

  protected onBulkUpdateOrderItemsClick(): void {
    this.isShowingBulkUpdateOrderItemsDialog = true;
  }

  protected onBulkUpdatePurchaseOrderItemsDialogUpdated(): void {
    this.initialize();
    this.refreshRouterView();
  }

  protected async onExportPurchaseOrderButtonClick(): Promise<void> {
    this.isExportingPurchaseOrder = true;

    await this.exportPurchaseOrder();

    this.isExportingPurchaseOrder = false;
  }

  protected onProductCreated(): void {
    this.initialize();
    this.refreshRouterView();
  }

  protected onPurchaseOrderCsvUploaded(): void {
    this.initialize();
    this.refreshRouterView();
  }

  protected onPurchaseOrderItemCreated(): void {
    this.isShowingCreatePurchaseOrderItemDialog = false;
    this.initialize();
    this.refreshRouterView();
  }

  // #endregion

  // #region Helpers

  protected getDialogOptionsUpdatePurchaseOrderStatus(status: OrderStatusEnum): Options {
    const statusLabel = OrderStatusKeyInfo[status]
      && OrderStatusKeyInfo[status].label
        ? OrderStatusKeyInfo[status].label
        : status;

    return {
      title: `Confirm status change to ${statusLabel}`,
      text: `Are you sure you wisth to change the status to ${statusLabel}`,
      type: 'warning',
      buttons: {
        confirm: {
          text: `Set to ${statusLabel}`,
          action: async (): Promise<void> => await this.updatePurchaseOrderStatus(status),
        },
        cancel: {
          text: 'Cancel',
        },
      },
    };
  }

  protected refreshRouterView(): void {
    if (! this.routerView || ! isFunction(this.routerView.refresh)) { return; }

    this.routerView.refresh();
  }

  // #endregion


  // #region API Requests

  protected async exportPurchaseOrder(): Promise<void> {
    if (!this.purchaseOrder || !this.purchaseOrder.id) { return; }

    try {
      await axiosDownload(
        {
          url: new PurchaseOrderModel().exportPurchaseOrder(this.purchaseOrder.id),
          accept: 'text/csv; charset=utf-8'
        },
        this.exportPurchaseOrderPayload,
      );
    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  protected async fetchPurchaseOrder(): Promise<void> {
    try {
      this.purchaseOrder = await new PurchaseOrderModel()
        .include(['brand', 'note', 'supplier'])
        .find(this.$route.params.id);
    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  protected async updatePurchaseOrderStatus(status: OrderStatusEnum): Promise<void> {
    if (!this.purchaseOrder) { return; }

    const purchaseOrder = cloneDeep(this.purchaseOrder);

    purchaseOrder.status = status;

    try {
      this.purchaseOrder = await this.purchaseOrder
        .include(['brand', 'note', 'supplier'])
        .update(purchaseOrder);
    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  // #endregion

  // #region Getters & Setters

  protected get doneButton() {
    if (!this.purchaseOrder) {
      return {
        label: '',
        action: null,
      }
    }

    switch (this.purchaseOrder.status) {
      case OrderStatusEnum.DRAFT:
        return {
          label: 'Set to pending',
          action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.PENDING)),
        }
      case OrderStatusEnum.PENDING:
        return {
          label: 'Set to confirmed',
          action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.CONFIRMED)),
        }
      case OrderStatusEnum.CONFIRMED:
        return {
          label: 'Set to submitted',
          action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.SUBMITTED)),
        }
      case OrderStatusEnum.SUBMITTED:
        // Set via webhooks by PVX
        return {
          label: '',
          action: null,
        }

      case OrderStatusEnum.RECEIVING:
        return {
          label: 'Set to completed',
          action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.COMPLETE)),
        }
        default:
        return {
          label: '',
          action: null,
        }
    }
  }

  protected get actionItems(): ActionItem[] {
    return [
      // {
      //   icon: 'undo',
      //   label: 'Set to draft',
      //   action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.DRAFT)),
      //   hidden: ! (
      //     this.purchaseOrder
      //     && this.purchaseOrder.status === OrderStatusEnum.CANCELLED
      //   ),
      // },
{
        icon: 'done',
        label: 'Set to complete',
        action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.COMPLETE)),
        hidden: ! (
          this.purchaseOrder
          && this.purchaseOrder.status === OrderStatusEnum.RECEIVING
        ),
      },
      // {
      //   icon: 'undo',
      //   label: 'Set back to draft',
      //   action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.DRAFT)),
      //   hidden: ! (
      //     this.purchaseOrder
      //     && this.purchaseOrder.status === OrderStatusEnum.PENDING
      //   ),
      // },
      // {
      //   icon: 'undo',
      //   label: 'Set back to pending',
      //   action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.PENDING)),
      //   hidden: ! (
      //     this.purchaseOrder
      //     && this.purchaseOrder.status === OrderStatusEnum.CONFIRMED
      //   ),
      // },
      // {
      //   icon: 'undo',
      //   label: 'Set back to confirmed',
      //   action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.CONFIRMED)),
      //   hidden: ! (
      //     this.purchaseOrder
      //     && this.purchaseOrder.status === OrderStatusEnum.SUBMITTED
      //   ),
      // },
      // {
      //   icon: 'undo',
      //   label: 'Set back to submitted',
      //   action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.SUBMITTED)),
      //   hidden: ! (
      //     this.purchaseOrder
      //     && this.purchaseOrder.status === OrderStatusEnum.RECEIVING
      //   ),
      // },
      // {
      //   icon: 'undo',
      //   label: 'Set back to receiving',
      //   action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.RECEIVING)),
      //   hidden: ! (
      //     this.purchaseOrder
      //     && this.purchaseOrder.status === OrderStatusEnum.COMPLETE
      //   ),
      // },
      {
        icon: 'cancel',
        label: 'Cancel order',
        action: () => this.$store.dispatch('openDialog', this.getDialogOptionsUpdatePurchaseOrderStatus(OrderStatusEnum.CANCELLED)),
        hidden: ! (
          this.purchaseOrder
          && this.purchaseOrder.status !== OrderStatusEnum.CANCELLED
        ),
      },
    ];
  }

  protected get OrderStatusEnum() {
    return OrderStatusEnum;
  }

  protected get exportPurchaseOrderPayload(): ExportPurchaseOrderItemsPayload {
    return {
      ...this.selectedPurchaseOrderItems.length
        ? {
          purchase_order_items: (this.selectedPurchaseOrderItems.map((purchaseOrderItem: PurchaseOrderItem) => purchaseOrderItem.id) as string[])
        }
        : {},
    }
  }

  protected get pageTitle(): string {
    return `Purchase order ${this.purchaseOrder && this.purchaseOrder.name ? this.purchaseOrder.name : ''}`;
  }

  protected get canEditOrderItems(): boolean {
    return !! this.purchaseOrder
      && !! this.purchaseOrder.status
      && [
        OrderStatusEnum.DRAFT,
        OrderStatusEnum.PENDING,
        OrderStatusEnum.CONFIRMED,
      ].includes(this.purchaseOrder.status)
  }


  // #endregion

  // #endregion

  // #region @Watchers

  @Watch('$route')
  protected routeChanged(): void {
    this.selectedPurchaseOrderItems = [];
  }

  // #endregion
}

interface ExportPurchaseOrderItemsPayload {
  purchase_order_items?: string[];
}
